import { CommonModule, CurrencyPipe } from '@angular/common';
import { Component, effect, inject, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { CompanyContextService } from '../../../../core/services/company-context.service';
import { JobItem, SubJobItem } from '../../data/models/job.models';
import { DownloadCheckoutSessionData } from '../../data/models/job-form.models';
import { JobsService } from '../../data/services/jobs.service';
import { JobProcessingStateService } from '../../data/services/job-processing-state.service';

@Component({
  selector: 'app-jobs',
  standalone: true,
  imports: [CommonModule, CurrencyPipe],
  templateUrl: './jobs.html',
  styleUrl: './jobs.scss',
})
export class Jobs implements OnInit {
  private readonly jobsService = inject(JobsService);
  private readonly companyContextService = inject(CompanyContextService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly jobProcessingStateService = inject(JobProcessingStateService);

  readonly selectedCompanyId = this.companyContextService.selectedCompanyId;
  readonly isSuperAdmin = this.companyContextService.isSuperAdmin;

  jobs: JobItem[] = [];
  totalCount = 0;
  isLoading = false;
  errorMessage = '';
  showCostColumn = false;

  pageNumber = 1;
  pageSize = 50;
  totalPages = 1;

  isRequeuingId: number | null = null;
  submitMessage = '';
  showSubmitAlert = false;
  submitAlertIsSuccess = false;

  // Download dialog state
  downloadingJobId: number | null = null;
  showDownloadPaymentDialog = false;
  downloadDialogJob: JobItem | null = null;
  downloadDialogData: DownloadCheckoutSessionData | null = null;

  // Expanded sub-jobs state
  expandedJobIds = new Set<number>();
  subJobsMap: Record<number, SubJobItem[]> = {};
  subJobsLoadingMap: Record<number, boolean> = {};
  subJobsErrorMap: Record<number, string> = {};

  private jobsSubscription?: Subscription;

  constructor() {
    effect(() => {
      const companyId = this.selectedCompanyId();
      this.pageNumber = 1;
      this.subscribeToJobs(companyId);
    });
  }

  getSafeAmount(value: number | undefined | null): number {
    if (value === undefined || value === null || isNaN(value)) return 0;
    if (value < 0) {
      console.warn(`[Jobs] Negative value detected for amount: ${value}. Displaying $0.00 instead.`);
      return 0;
    }
    return value;
  }

  ngOnInit(): void {
    const isPopup = this.route.snapshot.queryParamMap.get('popup') === 'true';
    if (isPopup) {
      // Close immediately if it's a popup return
      window.close();
    }

    const paymentSuccess = this.route.snapshot.queryParamMap.get('payment_success') === 'true';
    if (paymentSuccess) {
      this.submitMessage = 'Your payment was successful and your download is ready now. Click on the download button again to start downloading.';
      this.submitAlertIsSuccess = true;
      this.showSubmitAlert = true;

      // Clean up URL
      this.router.navigate([], {
        queryParams: { payment_success: null },
        queryParamsHandling: 'merge',
        replaceUrl: true
      });
    }

    const paymentCanceled = this.route.snapshot.queryParamMap.get('payment_canceled') === 'true';
    if (paymentCanceled) {
      this.submitMessage = 'Your payment was canceled.';
      this.submitAlertIsSuccess = false;
      this.showSubmitAlert = true;

      this.router.navigate([], {
        queryParams: { payment_canceled: null },
        queryParamsHandling: 'merge',
        replaceUrl: true
      });
    }
  }

  subscribeToJobs(companyId: number): void {
    this.jobsSubscription?.unsubscribe();

    this.isLoading = true;
    this.errorMessage = '';

    this.jobsSubscription = this.jobsService.getJobsWithRefresh(companyId, this.pageNumber, this.pageSize).subscribe({
      next: (response) => {
        this.jobs = response.items;
        this.totalCount = response.totalCount;
        this.totalPages = response.totalPages || Math.ceil(response.totalCount / this.pageSize) || 1;
        this.isLoading = false;
      },
      error: () => {
        this.errorMessage = 'Failed to load jobs.';
        this.isLoading = false;
      },
    });
  }

  previousPage(): void {
    if (this.pageNumber > 1) {
      this.pageNumber--;
      this.subscribeToJobs(this.selectedCompanyId());
    }
  }

  nextPage(): void {
    if (this.pageNumber < this.totalPages) {
      this.pageNumber++;
      this.subscribeToJobs(this.selectedCompanyId());
    }
  }

  onCreateJob(): void {
    this.jobProcessingStateService.clearActiveCreateJobId();
    this.router.navigate(['/app/jobs/create']);
  }

  onDetail(job: JobItem): void {
    this.jobProcessingStateService.clearActiveCreateJobId();
    this.router.navigate(['/app/jobs', job.id]);
  }

  canShowDownload(job: JobItem): boolean {
    return !!job.canDownload && (job.status === 'Completed' || job.status === 'PartiallyCompleted');
  }

  onDownload(job: JobItem): void {
    if (!this.canShowDownload(job) || this.downloadingJobId === job.id) return;

    this.downloadingJobId = job.id;
    const companyId = this.selectedCompanyId();

    const successUrl = window.location.origin + '/app/jobs?payment_success=true';
    const cancelUrl = window.location.origin + '/app/jobs?payment_canceled=true';

    this.jobsService.getDownloadCheckoutSession(job.id, companyId, successUrl, cancelUrl).subscribe({
      next: (res) => {
        this.downloadingJobId = null;

        if (!res.success || !res.data) {
          this.submitMessage = res.message || 'Download is not available for this job.';
          this.submitAlertIsSuccess = false;
          this.showSubmitAlert = true;
          return;
        }

        const data = res.data;

        // Owner company (companyId = 1) or payment already complete → direct download
        if (data.paymentSkipped || !data.requiresPayment) {
          if (data.downloadUrl) {
            window.open(data.downloadUrl, '_blank');
          } else {
            this.submitMessage = 'Download URL is not available for this job.';
            this.submitAlertIsSuccess = false;
            this.showSubmitAlert = true;
          }
          return;
        }

        // Non-owner: show payment dialog with remaining amount
        this.downloadDialogJob = job;
        this.downloadDialogData = data;
        this.showDownloadPaymentDialog = true;
      },
      error: () => {
        this.downloadingJobId = null;
        this.submitMessage = 'Failed to initiate download. Please try again.';
        this.submitAlertIsSuccess = false;
        this.showSubmitAlert = true;
      }
    });
  }

  get downloadAdvancePaid(): number {
    if (!this.downloadDialogJob || !this.downloadDialogData) return 0;
    const advance = this.downloadDialogJob.estimatedCostOfJob - this.downloadDialogData.remainingAmount;
    return Math.max(0, advance);
  }

  onPayDownloadNow(): void {
    if (this.downloadDialogData?.checkoutUrl && this.downloadDialogJob) {
      const checkoutUrl = this.downloadDialogData.checkoutUrl;

      // Redirect in the same tab
      window.location.href = checkoutUrl;
    }
  }


  closeDownloadPaymentDialog(): void {
    this.showDownloadPaymentDialog = false;
    this.downloadDialogJob = null;
    this.downloadDialogData = null;
  }

  onRequeue(job: JobItem): void {
    if (this.isRequeuingId === job.id) return;
    if (!job.canRequeue || !job.failedStage) {
      this.submitMessage = 'Failed stage information is missing for this job.';
      this.submitAlertIsSuccess = false;
      this.showSubmitAlert = true;
      return;
    }

    this.isRequeuingId = job.id;
    this.jobsService.requeueFailedStage(job.id, job.failedStage).subscribe({
      next: (res) => {
        this.isRequeuingId = null;
        this.submitMessage = res.message || 'Job requeued successfully.';
        this.submitAlertIsSuccess = true;
        this.showSubmitAlert = true;

        // Update the status locally based on the response stage
        if (res.success && res.data) {
          job.status = res.data.stage;
          job.canRequeue = false;
        }

        // Refresh the list after a short delay to ensure backend state is reflected
        setTimeout(() => {
          this.subscribeToJobs(this.selectedCompanyId());
        }, 1500);
      },
      error: (err) => {
        this.isRequeuingId = null;
        this.submitMessage = err.error?.message || 'Unable to requeue the failed job right now. Please try again.';
        this.submitAlertIsSuccess = false;
        this.showSubmitAlert = true;
      }
    });
  }

  closeSubmitAlert(): void {
    this.showSubmitAlert = false;
  }

  isExpanded(jobId: number): boolean {
    return this.expandedJobIds.has(jobId);
  }

  toggleExpand(jobId: number): void {
    if (this.expandedJobIds.has(jobId)) {
      this.expandedJobIds.delete(jobId);
    } else {
      this.expandedJobIds.add(jobId);
      this.fetchSubJobs(jobId);
    }
  }

  fetchSubJobs(jobId: number): void {
    this.subJobsLoadingMap[jobId] = true;
    this.subJobsErrorMap[jobId] = '';

    this.jobsService.getSubJobs(jobId).subscribe({
      next: (res) => {
        this.subJobsLoadingMap[jobId] = false;
        if (res.success && res.data) {
          this.subJobsMap[jobId] = res.data;
        } else {
          this.subJobsMap[jobId] = [];
        }
      },
      error: () => {
        this.subJobsLoadingMap[jobId] = false;
        this.subJobsErrorMap[jobId] = 'Failed to load sub-jobs.';
      }
    });
  }

  getJobStatusDisplay(status?: string): string {
    if (!status) return 'Draft';
    if (status === 'PartiallyCompleted') return 'Completed';
    return status;
  }

  getSubJobStatusDisplay(status?: string): string {
    if (!status) return '';
    if (status.toLowerCase() === 'created') {
      return 'Ready to process';
    }
    return status;
  }
}
